// How the event senders (the batched event queue and the play-time note flush)
// treat a failed POST to the events endpoint: which failures a resend can fix,
// how long to wait before the next try, and when to give up.

// MUS-636 numbers. A send becomes due again 1-2s after its first failure, so the
// first resend goes on the next flush when the failed POST was quick, or the one
// after. The gap then doubles up to a ceiling of 30-60s. Eight resends span about
// 2 to 4 minutes of trying, which outlasts a database failover or a deploy
// rollover, while a send the database will never take costs 9 POSTs instead of one
// every flush for the rest of the page's life.
export const MAX_RESENDS = 8;
const RESEND_BASE_DELAY_MS = 2000;
const RESEND_MAX_DELAY_MS = 60_000;

// Equal jitter: half of the exponential step is fixed and half is random. The
// shortest possible gap doubles with each failed send until the ceiling, so the
// gaps grow on the whole; at the ceiling they vary between 30 and 60s. Players
// whose sends failed at the same moment spread their resends out instead of
// retrying in lockstep.
export const resendDelayMs = (failedSends: number): number => {
  const step = Math.min(RESEND_MAX_DELAY_MS, RESEND_BASE_DELAY_MS * 2 ** (failedSends - 1));
  return step / 2 + Math.random() * (step / 2);
};

// 4xx statuses that mean "try again" rather than "this request is wrong": an auth
// token the interceptor could not refresh in time, a request timeout, a rate limit.
const RETRYABLE_CLIENT_ERROR_STATUSES = [401, 408, 429];

// Any other 4xx is a permanent reject: the server would answer a resend the same
// way (on the events endpoint's own 422 it has already persisted every event it
// could), so the events are dropped and reported, never resent.
export const isPermanentReject = (status: number): boolean =>
  status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERROR_STATUSES.includes(status);

// A send that failed with no response while the device says it is offline waits
// like any other but does not count toward MAX_RESENDS.
export const isDeviceOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;
